import * as React from "react";
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Link,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {useEffect} from "react";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import {Helmet} from "react-helmet-async";

type ExperienceItem = {
    role: string;
    orgAndLocation: string;
    dates: string;
    summary: React.ReactNode;
    bullets: React.ReactNode[];
};

type ProjectItem = {
    name: string;
    dates: string;
    links?: { label: string; href?: string }[];
    summary: React.ReactNode;
    bullets: React.ReactNode[];
};

function Section({
                     title,
                     children,
                 }: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Box component="section" sx={{ mb: 4 }}>
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    mb: 1,
                }}
            >
                {title}
            </Typography>
            <Divider sx={{ mb: 2, opacity: 0.6 }} />
            {children}
        </Box>
    );
}

function EntryHeader({
                         leftTitle,
                         leftSubtitle,
                         rightMeta,
                     }: {
    leftTitle: string;
    leftSubtitle: string;
    rightMeta: string;
}) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
            sx={{ mb: 1 }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 800, color: '#101828 !important' }}>
                    {leftTitle}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.85, color: '#101828 !important' }}>
                    {leftSubtitle}
                </Typography>
            </Box>

            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    opacity: 0.75,
                    whiteSpace: "nowrap",
                    color: '#101828 !important'
                }}
            >
                {rightMeta}
            </Typography>
        </Stack>
    );
}

export default function Resume() {
    useEffect(() => {
        const prev = document.title;
        document.title = "Resume - Aggrey Lelei";

        return () => {
            document.title = prev;
        };
    }, []);
    const header = {
        name: "Aggrey Kiplimo Lelei",
        title: "Head Of Technology",
        email: "aggreykiplimo@gmail.com", // keep as provided
        emailHref: "mailto:aggreykiplimo@gmail.com",
        linkedinLabel: "linkedin.com/in/aggrey-lelei",
        linkedinHref: "https://www.linkedin.com/in/aggrey-lelei",
        websiteLabel: "aggreylelei.com",
        websiteHref: "https://aggreylelei.com",
    };
    const experiences: ExperienceItem[] = [
        {
            role: "Head of Technology",
            orgAndLocation: "Tiret Resorts • Kenya",
            dates: "March 2023 - Present",
            summary: (
              <>
                Promoted to Principal Lead during platform scale-out and growing engineering needs, owning
                technical strategy, system architecture, and delivery while expanding
                managerial scope across recruiting, mentorship, and stakeholder alignment.
              </>
            ),
            bullets: [
              <>
                <strong>Technical Strategy & Architecture:</strong> Owned platform direction and
                system design for scale, improving NGO productivity and engagement by ~55% and
                increasing regional coordination impact through reliable platform execution.
              </>,

              <>
                <strong>Platform Expansion & Outcomes:</strong> Pitched and delivered a major
                platform expansion that drove revenue growth, improved customer satisfaction,
                and increased regional engagement through clearer workflows and stronger
                reliability.
              </>,
              <>
                <strong>People Leadership:</strong> Led a team of engineers; handled
                recruiting, onboarding, mentoring, and performance support while maintaining
                high engineering throughput and quality.
              </>,
                <>
                    <strong>Performance & Scale:</strong> Supported multiple concurrent users with
                    <strong> sub-150 ms latency</strong> at peak, emphasizing fast user experiences
                    and resilient operations.
                </>,
              <>
                <strong>Stakeholder & Shareholder Management:</strong> Took ownership of
                cross-functional alignment — translating business goals into technical plans,
                leading shareholder discussions, and communicating trade-offs, risk, and
                delivery timelines with clarity.
              </>,
              <>
                <strong>Agentic Context Engineering:</strong> Integrated Embabel to enable advanced agentic
                and retrieval-augmented workflows grounded in live system data, strengthening
                operational decision-making and accelerating support/coordination loops.
              </>,
            ],
          },
        {
            role: "Senior Software Engineer",
            orgAndLocation: "Tiret Resorts • Kenya",
            dates: "January 2020 - Feb 2023",
            summary: (
                <>
                    Architected and delivered a cloud-native NGO communication and logistics platform from
                    inception to production, owning system design, backend architecture, testing strategy,
                    and long-term maintainability while leading day-to-day engineering execution.
                </>
            ),
            bullets: [
                <>
                    <strong>Spring Boot Architecture Design:</strong> Designed a modular Spring Boot backend
                    using layered architecture (Controller–Service–Repository), RESTful APIs, JPA/Hibernate,
                    and secure authentication flows. Established clean package structures, configuration
                    management, and scalable deployment foundations.
                </>,
                <>
                    <strong>Platform from Scratch:</strong> Built core coordination, messaging, and logistics
                    workflows end-to-end, translating business requirements into resilient domain models
                    and production-grade backend services.
                </>,
                <>
                    <strong>Testing & Code Quality:</strong> Implemented unit and integration testing
                    (JUnit, Mockito), enforced code reviews and standards, and improved reliability
                    through structured validation and regression coverage.
                </>,
                <>
                    <strong>Maintenance & Optimization:</strong> Owned ongoing system maintenance,
                    refactoring legacy modules, resolving production issues, improving performance,
                    and ensuring long-term codebase sustainability.
                </>,
                <>
                    <strong>Team Leadership:</strong> Mentored and unblocked junior engineers, improved
                    development workflows, and maintained delivery discipline across the engineering lifecycle.
                </>
            ]
        },
        {
            role: "Android Software Engineer",
            orgAndLocation: "Smart Web Kenya • Nairobi, Kenya",
            dates: "September 2017 - December 2019",
            summary: (
                <>
                    Began career building and shipping production-grade Android applications,
                    delivering end-to-end mobile features, integrating backend APIs, and ensuring
                    stable releases for real-world client deployments.
                </>
            ),
            bullets: [
                <>
                    <strong>End-to-End Android Development:</strong> Designed and implemented
                    native Android applications using Java/Kotlin, following MVVM architecture,
                    clean code principles, and modern Android SDK best practices.
                </>,
                <>
                    <strong>API Integration & Data Handling:</strong> Integrated RESTful APIs,
                    implemented JSON parsing, managed local persistence (Room/SQLite),
                    and ensured efficient state management for reliable offline/online behavior.
                </>,
                <>
                    <strong>UI/UX & Performance Optimization:</strong> Built responsive,
                    user-friendly interfaces using XML layouts and Android components,
                    optimized rendering performance, and reduced crashes through
                    structured debugging and profiling.
                </>,
                <>
                    <strong>Release & Production Stability:</strong> Managed build variants,
                    signing configurations, Play Store deployment processes, and
                    strengthened error handling to maintain stability in live environments.
                </>,
                <>
                    <strong>Engineering Foundations:</strong> Developed strong fundamentals in
                    debugging, performance tuning, modular code organization, and disciplined
                    feature delivery forming the base for later backend and distributed systems work.
                </>
            ],
        },
    ];
    const projects: ProjectItem[] = [
        {
            name: "SparrowX - Enterprise Agentic Knowledge | Internal Company Directory",
            dates: "2025 – Present",
            links: [
                { label: "📁 Source Code", href: "https://github.com/hashedgrey/Sparrow-x" },
            ],
            summary: (
                <>
                    An enterprise agentic knowledge system for engineering organizations,
                    combining durable multi-step AI orchestration with structured company context,
                    document intelligence, grounded reasoning, and self-referential developer onboarding.
                </>
            ),
            bullets: [
                <> <strong>Durable agentic orchestration:</strong> Built the Agentic Service around
                    Embabel for intent interpretation, iterative planning/review, tool selection,
                    and synthesis, with Temporal providing durable workflow execution, retries,
                    cancellation, human approval gates, checkpoints, and crash recovery.
                </>,
                <> <strong>Enterprise knowledge architecture:</strong> Designed gRPC-based Agentic,
                    Internal, and Document services; Internal models teams, services, ownership,
                    repositories, and onboarding paths, while Document provides ingestion,
                    retrieval, and evidence generation for unstructured engineering knowledge.
                </>,
                <> <strong>Grounded retrieval & context engineering:</strong> Implemented hybrid
                    retrieval across Elasticsearch and Qdrant, evidence graph construction,
                    citation and grounding verification, and multi-hop reasoning across structured
                    internal data and technical documents.
                </>,
                <> <strong>Production infrastructure & LLMOps:</strong> Built shared CQRS
                    infrastructure, multi-tenant governance, PostgreSQL/Neo4j/MinIO-backed services,
                    OpenTelemetry observability with Tempo, Prometheus, Loki, and Grafana, plus
                    Langfuse for agent tracing, cost/latency analysis, prompt management, and evals.
                </>,
            ],
        }

    ];

    return (
        <>
            <Helmet>
                <title>Aggrey Lelei Resume | Head Of Tech</title>
                <meta
                    name="description"
                    content="Software Architect, Tech Lead and Backend engineer specializing in Microservices, Ai Orchestration, Event Driven Systems and Distributed Systems. View Aggrey Lelei's resume and projects."
                />
                <link rel="canonical" href="https://aggreylelei.com/resume/" />
            </Helmet>
            <Box
                sx={{
                    py: { xs: 3, md: 5 },

                    "@media print": {
                        py: 0,
                        bgcolor: "#101828",
                    },
                }}
            >
                <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 5 },
                            borderRadius: 2,
                            backgroundColor: "#fff",
                            color: "#101828 !important",
                            "@media print": {
                                boxShadow: "none",
                                p: 0,
                                borderRadius: 0,
                            },
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ textAlign: "center", mb: 3 }}>
                            <Typography variant="h4"
                                        sx={{ fontWeight: 620,
                                            mb: 0.5,
                                            color: '#232946 !important',
                                            fontSize: '2.6rem',
                                            fontFamily:"system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                                        }}>
                                {header.name}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: 2,
                                    fontWeight: 600,
                                    opacity: 0.9,
                                    mb: 2,
                                    fontSize: '1.4rem',
                                    color: '#232946 !important',
                                    fontFamily:"system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
                                }}
                            >
                                {header.title}
                            </Typography>

                            <Stack
                                direction="row"
                                useFlexGap
                                flexWrap="wrap"
                                justifyContent="center"
                                alignItems="center"
                                spacing={2}
                                sx={{ color: "#232946" }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <EmailOutlinedIcon fontSize="small" />
                                    <Link
                                        href={header.emailHref}
                                        underline="hover"
                                        color="primary"
                                        sx={{color: "#232946"}}
                                    >
                                        {header.email}
                                    </Link>
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <LinkOutlinedIcon fontSize="small" />
                                    <Link
                                        href={header.linkedinHref}
                                        target="_blank"
                                        rel="noopener"
                                        underline="hover"
                                        color="primary"
                                        sx={{color: "#232946"}}
                                    >
                                        {header.linkedinLabel}
                                    </Link>
                                </Stack>

                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <LanguageOutlinedIcon fontSize="small" />
                                    <Link
                                        href={header.websiteHref}
                                        target="_blank"
                                        rel="noopener"
                                        underline="hover"
                                        color="primary"
                                        sx={{color: "#232946"}}
                                    >
                                        {header.websiteLabel}
                                    </Link>
                                </Stack>
                            </Stack>

                            <Stack
                                direction="row"
                                justifyContent="center"
                                spacing={1.5}
                                sx={{
                                    mt: 3,
                                    //"@media print": { display: "none" },
                                }}
                            >
                                <Button

                                    variant="contained"
                                    startIcon={<PrintOutlinedIcon />}
                                    onClick={() => window.print()}
                                    sx={{
                                        textTransform: 'none',
                                        background: "#232946 !important"}}
                                >
                                    Print Resume
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<SendOutlinedIcon />}
                                    href="/#contact"
                                    sx={{
                                        textTransform: 'none',
                                        color: "#232946 !important",
                                        borderBlockColor: "#232946" }}
                                >
                                    Contact Me
                                </Button>
                            </Stack>

                            <Divider sx={{ mt: 3 }} />
                        </Box>

                        {/* Professional Summary */}
                        <Section title="Professional Summary">
                            <Typography variant="body2" className="article-text" sx={{ lineHeight: 1.7, color: "#232946" }}>
                                Head of Technology and Senior Software Engineer with 8+ years of experience leading teams and building
                                scalable distributed systems. After early career work in Android and ERP development at Smart Web Kenya,
                                I joined Tiret Resorts in 2020 to architect a cloud-native NGO logistics platform from scratch. Following
                                a major expansion pitch that drove revenue and regional adoption, I was promoted to Head of Technology to
                                lead engineering strategy and platform scale-out. Today, I direct a small engineering team operating
                                Spring Boot–based event-driven services that supports multiple concurrent users at peak  with sub-150 ms
                                latency while improving NGO productivity by ~55%. My focus centers on resilient backpressure-aware
                                processing, deep system observability, and integrating Agentic AI to power agentic RAG workflows
                                grounded in live data.
                            </Typography>
                        </Section>

                        {/* Experience */}
                        <Section title="Experience">
                            {experiences.map((e, idx) => (
                                <Box key={idx}
                                     sx={{ mb: idx === experiences.length - 1 ? 0 : 3,

                                }}>
                                    <EntryHeader
                                        leftTitle={e.role}
                                        leftSubtitle={e.orgAndLocation}
                                        rightMeta={e.dates}
                                    />
                                    <Typography variant="body2"
                                                sx={{ mb: 1.5, fontWeight: 550, opacity: 0.9,
                                                    color: '#232946 !important'}}>
                                        {e.summary}
                                    </Typography>

                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {e.bullets.map((b, i) => (
                                            <Box
                                                component="li"
                                                key={i}
                                                sx={{
                                                    mb: 0.75,
                                                    typography: "caption",
                                                    color: "#232946",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {b}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Section>

                        {/* Technical Skills */}
                        <Section title="Technical Skills">
                            <Stack spacing={1.25}>
                                {[
                                    {
                                        k: "Languages:",
                                        v: "Springboot (Java + Kotlin), Python, TypeScript, Go",
                                    },
                                    { k: "Enterprise AI Orchestration:", v: "Embabel, LangGraph" },
                                    { k: "Workflow Orchestration:", v: "Temporal, Restate" },
                                    { k: "Event Streaming:", v: "Kafka, RabbitMQ" },
                                    {
                                        k: "Cloud & Infrastructure:",
                                        v: "AWS (S3, EKS, MSK, ElastiCache, RDS, ECS, SQS, CloudWatch), Azure Cloud, Docker, k8s, Google Cloud Platform",
                                    },
                                    { k: "Secret Management:", v: "HashiCorp  Vault" },
                                    { k: "Monitoring:", v: "LangFuse, Prometheus, Loki, Tempo, Alloy, Grafana" },
                                ].map((row) => (
                                    <Stack direction="row" flexWrap="wrap" gap={1} key={row.k}>
                                        <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                            {row.k}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "text.primary", opacity: 0.85 }}>
                                            {row.v}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Section>

                        {/* Projects */}
                        <Section title="Projects">
                            {projects.map((p, idx) => (
                                <Box key={idx} sx={{ mb: idx === projects.length - 1 ? 0 : 3 }}>
                                    <EntryHeader
                                        leftTitle={p.name}
                                        leftSubtitle=""
                                        rightMeta={p.dates}
                                    />

                                    {!!p.links?.length && (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                            {p.links.map((l, i) =>
                                                l.href ? (
                                                    <Chip
                                                        key={i}
                                                        component="a"
                                                        clickable
                                                        href={l.href}
                                                        target="_blank"
                                                        rel="noopener"
                                                        label={l.label}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ textDecoration: "none" }}
                                                    />
                                                ) : (
                                                    <Chip key={i} label={l.label} variant="outlined" size="small" />
                                                )
                                            )}
                                        </Stack>
                                    )}

                                    <Typography variant="body2" sx={{ mb: 1.5, color: "text.primary", opacity: 0.9 }}>
                                        {p.summary}
                                    </Typography>

                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {p.bullets.map((b, i) => (
                                            <Box
                                                component="li"
                                                key={i}
                                                sx={{
                                                    mb: 0.75,
                                                    typography: "caption",
                                                    color: "text.primary",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {b}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Section>

                        {/* Credentials */}
                        <Section title="Credentials">

                            <Typography
                                component="a"
                                href="https://www.credly.com/badges/3856740c-7196-4fc9-b0d3-9f60c2ac46c4/public_url"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{
                                    color: '#232946',
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    mb: 0.75,
                                    fontSize: '1rem',
                                    fontWeight: 550,
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                • IBM Blockchain Developer - Mastery Award (2018)
                                <LaunchRoundedIcon sx={{ fontSize: 18 }} />
                            </Typography>


                            <Typography variant="caption" sx={{ color: '#232946', display: "block", mb: 0.75, fontSize: '1rem', fontWeight: 550 }}>
                                • Bachelor's Degree in Computer Science - Jomo Kenyatta University Of Agriculture And Technology, Nairobi (2017)
                            </Typography>
                        </Section>

                        {/* Writing & Knowledge Sharing */}
                        {/*<Section title="Writing & Knowledge Sharing">*/}
                        {/*    {[*/}
                        {/*        {*/}
                        {/*            title: "“Let us talk about bitwise operations”",*/}
                        {/*            meta: "Medium, April 2025 • Computer Science Deep-Dive • ",*/}
                        {/*            linkLabel: "Read Article",*/}
                        {/*            linkHref:*/}
                        {/*                "https://blog.stanleymasinde.com/let-us-talk-bitwise-operations-2c3c350cf808",*/}
                        {/*            body:*/}
                        {/*                "Comprehensive exploration of bitwise operations from first principles, covering binary number systems, CPU-level optimizations, and practical applications. Demonstrates understanding of low-level computing concepts including bit manipulation, memory efficiency, and hardware-level programming with Rust examples and real-world use cases like feature flags and embedded systems.",*/}
                        {/*        },*/}
                        {/*        {*/}
                        {/*            title: "“SSH Deployment with GitHub Actions”",*/}
                        {/*            meta: "Medium, February 2023 • Monetized Article • ",*/}
                        {/*            linkLabel: "Read Article",*/}
                        {/*            linkHref:*/}
                        {/*                "https://blog.stanleymasinde.com/ssh-deployment-with-github-actions-f2274269d9f3",*/}
                        {/*            body:*/}
                        {/*                "Comprehensive technical guide solving secure CI/CD deployment challenges. Covers SSH key management in dynamic GitHub Actions environments, secure server access patterns, and automated deployment workflows. Includes practical implementation examples with Nuxt.js, RSYNC file transfer, and complete security configuration for production deployments.",*/}
                        {/*        },*/}
                        {/*    ].map((w) => (*/}
                        {/*        <Box key={w.title} sx={{ mb: 2.5 }}>*/}
                        {/*            <Typography variant="body1" sx={{ fontWeight: 800, mb: 0.5 }}>*/}
                        {/*                {w.title}*/}
                        {/*            </Typography>*/}
                        {/*            <Typography variant="caption" sx={{ opacity: 0.8 }}>*/}
                        {/*                {w.meta}*/}
                        {/*                <Link href={w.linkHref} target="_blank" rel="noopener" underline="hover">*/}
                        {/*                    {w.linkLabel}*/}
                        {/*                </Link>*/}
                        {/*            </Typography>*/}
                        {/*            <Typography variant="caption" sx={{ display: "block", mt: 0.75, lineHeight: 1.7 }}>*/}
                        {/*                {w.body}*/}
                        {/*            </Typography>*/}
                        {/*        </Box>*/}
                        {/*    ))}*/}
                        {/*</Section>*/}
                    </Paper>
                </Container>
            </Box>
        </>
    );
}
